import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const SearchForm = () => {
  const [filters, setFilters] = useState({
    baby_name: '',
    mother_name: '',
    // father_name: '',
    registration_id: '',
    district_id: '',
    township_id: '',
    clinic_id: '',
    start_date: '',
    end_date: '',
    page: 1,
    limit: 25
  });

  const [referenceData, setReferenceData] = useState({
    districts: [],
    townships: [],
    clinics: []
  });

  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load all records on component mount
  useEffect(() => {
    loadRecords();
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const districts = await api.referenceData.getDistricts().catch(() => []);

      setReferenceData({
        districts: districts || [],
        townships: [],
        clinics: []
      });
    } catch (error) {
      console.error('Error loading reference data:', error);
    }
  };

  const loadRecords = async (searchFilters = filters) => {
    setLoading(true);
    setError(null);
    
    try {

      const response = await api.birthRecords.getBirthRecords(searchFilters);

      
      if (response && response.data) {
        setRecords(response.data);
        setPagination(response.pagination || {
          page: searchFilters.page,
          limit: searchFilters.limit,
          total: response.data.length,
          pages: Math.ceil(response.data.length / searchFilters.limit)
        });
      } else {
        setRecords([]);
        setPagination({
          page: 1,
          limit: 25,
          total: 0,
          pages: 0
        });
      }
      setHasLoaded(true);
    } catch (err) {
      console.error('Error loading records:', err);
      setError(err.message || 'Failed to load birth records');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const updatedFilters = {
      ...filters,
      [name]: value,
      page: 1 // Reset to first page when filtering
    };
    setFilters(updatedFilters);
    
    // If changing the limit, reload records immediately
    if (name === 'limit') {
      loadRecords(updatedFilters);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadRecords(filters);
  };

  const handlePageChange = (newPage) => {
    const updatedFilters = { ...filters, page: newPage };
    setFilters(updatedFilters);
    loadRecords(updatedFilters);
  };

  const resetFilters = () => {
    const resetFilters = {
      baby_name: '',
      mother_name: '',
      // father_name: '',
      registration_id: '',
      district_id: '',
      township_id: '',
      clinic_id: '',
      start_date: '',
      end_date: '',
      page: 1,
      limit: 25
    };
    setFilters(resetFilters);
    loadRecords(resetFilters);
  };

  // Handle district change for filtering
  const handleDistrictChange = async (districtId) => {
    setFilters(prev => ({
      ...prev,
      district_id: districtId,
      township_id: '',
      clinic_id: '',
      page: 1
    }));

    if (districtId) {
      try {
        const townships = await api.referenceData.getTownshipsByDistrict(districtId);
        setReferenceData(prev => ({
          ...prev,
          townships: townships || [],
          clinics: []
        }));
      } catch (error) {
        console.error('Error loading townships:', error);
      }
    } else {
      setReferenceData(prev => ({
        ...prev,
        townships: [],
        clinics: []
      }));
    }
  };

  // Handle township change for filtering
  const handleTownshipChange = async (townshipId) => {
    setFilters(prev => ({
      ...prev,
      township_id: townshipId,
      clinic_id: '',
      page: 1
    }));

    if (townshipId) {
      try {
        const clinics = await api.referenceData.getClinicsByTownship(townshipId);
        setReferenceData(prev => ({
          ...prev,
          clinics: clinics || []
        }));
      } catch (error) {
        console.error('Error loading clinics:', error);
      }
    } else {
      setReferenceData(prev => ({
        ...prev,
        clinics: []
      }));
    }
  };

  // CSV Export functionality
  const exportToCSV = () => {
    if (records.length === 0) {
      alert('No records to export');
      return;
    }
    
    const headers = [
      'No', 'Org_Name', 'District', 'Township', 'Clinic', 'Date', 'ID',
      'Baby_Name', 'Sex', 'DOB', 'Week_of_Day', 'Time', 'Birth_Place_Choose', 'Birth_Place_Other',
      'BP_Village', 'BP_Township', 'BP_District', 'Birth_Type', 'Twin_Order', 'Birth_Order',
      'Father_Name', 'Father_National_ID', 'Father_Ethnic', 'Father_Ethnic_Other_Specify', 'Father_Religion', 'Father_Address',
      'Mother_Name', 'Mother_National_ID', 'Mother_Ethnic', 'Mother_Ethnic_Other_Specify', 'Mother_Religion', 'Mother_Address',
      'Delivered_By_Name', 'Delivered_By_Position', 'Delivered_By_Signature', 'Delivered_By_Address',
      'Delivery_Type', 'Weight', 'Height', 'Head_Circumference', 'Birth_Defected', 'Defect_Description', 'Mother_Thumb', 'Father_Thumb', 'Baby_Foot',
      'Certified_By_Name', 'Certified_By_Position', 'Certified_By_Signature', 'Certified_By_Clinic',
      'Created_By', 'Created_By_Name'
    ];
    
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    const formatBirthTime = (hour, minute, ampm, fallback = '') => {
      if (hour === null || hour === undefined || hour === '' || minute === null || minute === undefined || minute === '') {
        return fallback;
      }

      return `${hour}:${String(minute).padStart(2, '0')} ${ampm || 'AM'}`;
    };
    
    const csvContent = [
      headers.join(','),
      ...records.map((record, index) => {
        const birthTime = formatBirthTime(record.birth_time_hour, record.birth_time_minute, record.birth_time_ampm);
        
        return [
          ((pagination.page - 1) * pagination.limit) + index + 1,
          `"${record.organization_name || ''}"`,
          `"${record.district_name || ''}"`,
          `"${record.township_name || ''}"`,
          `"${record.clinic_name || ''}"`,
          formatDate(record.registration_date),
          `"${record.registration_id || ''}"`,
          `"${record.baby_name || ''}"`,
          `"${record.gender || ''}"`,
          formatDate(record.birth_date),
          `"${record.week_of_day || ''}"`,
          `"${birthTime}"`,
          `"${getBirthPlaceName(record.birth_place_choose)}"`,
          `"${record.birth_place_other_specify || ''}"`,
          `"${record.birth_place_district_name?.toLowerCase() === 'other' ? (record.birth_place_other_village || '') : (record.birth_place_village_name || record.birth_place_village || '')}"`,
          `"${record.birth_place_district_name?.toLowerCase() === 'other' ? (record.birth_place_other_township || '') : (record.birth_place_township_name || record.birth_place_township || '')}"`,
          `"${record.birth_place_district_name?.toLowerCase() === 'other' ? (record.birth_place_other_district || '') : (record.birth_place_district_name || record.birth_place_district || '')}"`,
          `"${record.birth_type || ''}"`,
          `"${record.twin_order || ''}"`,
          `"${record.birth_order || ''}"`,
          `"${record.father_name || ''}"`,
          `"${record.father_nrc_formatted || record.father_national_id || ''}"`,
          `"${record.father_ethnicity || ''}"`,
          `"${record.father_ethnicity_other_specify || ''}"`,
          `"${record.father_religion || ''}"`,
          `"${record.father_address || ''}"`,
          `"${record.mother_name || ''}"`,
          `"${record.mother_nrc_formatted || record.mother_nrc || ''}"`,
          `"${record.mother_ethnicity || ''}"`,
          `"${record.mother_ethnicity_other_specify || ''}"`,
          `"${record.mother_religion || ''}"`,
          `"${record.mother_address || ''}"`,
          `"${record.delivered_by_name || ''}"`,
          `"${record.delivered_by_position || ''}"`,
          `"${record.delivered_by_signature || ''}"`,
          `"${record.delivered_by_address || ''}"`,
          `"${record.delivery_type || ''}"`,
          `"${record.baby_weight || ''}"`,
          `"${record.baby_height || ''}"`,
          `"${record.baby_head_circumference || ''}"`,
          `"${record.baby_birth_defected ? 'Yes' : 'No'}"`,
          `"${(record.birth_defect_description || '').replace(/"/g, '""')}"`,
          `"${record.mother_right_thumb_print ? 'Yes' : 'No'}"`,
          `"${record.father_right_thumb_print ? 'Yes' : 'No'}"`,
          `"${record.baby_right_foot_print ? 'Yes' : 'No'}"`,
          `"${record.recorder_name || ''}"`,
          `"${record.recorder_position || ''}"`,
          `"${record.recorder_signature ? 'Yes' : 'No'}"`,
          `"${record.recorder_clinic_name || ''}"`,
          `"${record.created_by || ''}"`,
          `"${record.created_by_name || ''}"`
        ].join(',');
      })
    ].join('\n');
    
    // Add BOM (Byte Order Mark) for proper UTF-8 encoding in Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `birth_records_search_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // Helper function to get birth place name from ID
  const getBirthPlaceName = (birthPlaceValue) => {
    if (!birthPlaceValue) return '-';
    
    // If it's already a name (not a number), return it
    if (isNaN(birthPlaceValue)) return birthPlaceValue;
    
    // Match IDs from tbl_birth_places seed data:
    // 1=Home, 2=Hospital, 3=Clinic, 4=Other
    const birthPlaceMap = {
      1: 'Home',
      2: 'Hospital',
      3: 'Clinic',
      4: 'Other'
    };
    
    return birthPlaceMap[birthPlaceValue] || birthPlaceValue;
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-white shadow-lg rounded-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Birth Records</h2>
        <p className="text-gray-600">View and search all birth records</p>
      </div>

      {/* Search Filters */}
      <form onSubmit={handleSearch} className="mb-6 bg-gray-50 p-4 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              District
            </label>
            <select
              name="district_id"
              value={filters.district_id}
              onChange={(e) => handleDistrictChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Districts</option>
              {referenceData.districts.map(district => (
                <option key={district.district_id} value={district.district_id}>{district.district_desc}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Township
            </label>
            <select
              name="township_id"
              value={filters.township_id}
              onChange={(e) => handleTownshipChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!filters.district_id}
            >
              <option value="">All Townships</option>
              {referenceData.townships.map(township => (
                <option key={township.township_id} value={township.township_id}>{township.township_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clinic
            </label>
            <select
              name="clinic_id"
              value={filters.clinic_id}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!filters.township_id}
            >
              <option value="">All Clinics</option>
              {referenceData.clinics.map(clinic => (
                <option key={clinic.clinic_id} value={clinic.clinic_id}>{clinic.clinic_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Birth Date From
            </label>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              title="Filter by birth date range - start date"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Birth Date To
            </label>
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              title="Filter by birth date range - end date"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span>Search</span>
          </button>
          
          <button
            type="button"
            onClick={resetFilters}
            className="px-4 py-2 bg-gray-600 text-white rounded-md font-medium hover:bg-gray-700 transition-colors"
          >
            Reset
          </button>

          <button
            type="button"
            onClick={exportToCSV}
            disabled={loading || records.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-md font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Export CSV</span>
          </button>

          <div className="ml-auto flex items-center space-x-2">
            <label htmlFor="limit" className="text-sm font-medium text-gray-700">
              Show:
            </label>
            <select
              id="limit"
              name="limit"
              value={filters.limit}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span className="text-sm text-gray-600">records</span>
          </div>
        </div>
      </form>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading records...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-800 font-medium">Error loading records</span>
          </div>
          <p className="text-red-700 mt-2">{error}</p>
          <button
            onClick={() => loadRecords()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Results Summary */}
      {hasLoaded && !loading && (
        <div className="mb-4 flex justify-between items-center">
          <p className="text-gray-600">
            {pagination.total > 0 ? (
              <>
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
              </>
            ) : (
              'No records found'
            )}
          </p>
          
          {records.length > 0 && (
            <div className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.pages}
            </div>
          )}
        </div>
      )}

      {/* Records Table */}
      {hasLoaded && !loading && records.length > 0 && (
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
          <table className="min-w-full text-xs border-collapse border border-gray-300 table-auto">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="bg-gray-100">
                {/* Basic Information - using rowSpan to merge across both header rows */}
                <th className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700" rowSpan="2">No</th>
                <th className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700" rowSpan="2">Org Name</th>
                <th className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700" rowSpan="2">District</th>
                <th className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700" rowSpan="2">Township</th>
                <th className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700" rowSpan="2">Clinic</th>
                <th className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700" rowSpan="2">Date</th>
                <th className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700" rowSpan="2">ID</th>
                
                {/* Baby Information */}
                <th className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700 bg-blue-50" colSpan="13">Baby Information</th>
                
                {/* Father Information */}
                <th className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700 bg-green-50" colSpan="5">Father Information</th>
                
                {/* Mother Information */}
                <th className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700 bg-yellow-50" colSpan="5">Mother Information</th>
                
                {/* Delivered By */}
                <th className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700 bg-orange-50" colSpan="4">Delivered By</th>
                
                {/* Birth Records */}
                <th className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700 bg-purple-50" colSpan="9">Birth Records</th>
                
                {/* Certified By */}
                <th className="border border-gray-300 px-2 py-2 text-center font-medium text-gray-700 bg-indigo-50" colSpan="4">Certified By</th>
              </tr>
              <tr className="bg-gray-50">
                {/* Basic Information Headers - removed since they're merged with rowSpan above */}
                
                {/* Baby Information Headers */}
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50">Name</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50">Sex</th>
                {/* <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50">Hospital/Clinic ID</th> */}
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50">DOB</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50">Week of Day</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50">Time</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50">Birth Place Choose</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50">Birth Place Other</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50">BP Village</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50">BP Township</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50">BP District</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50">Birth Type</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50">Twin Order</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-blue-50"># Birth Order</th>
                
                {/* Father Information Headers */}
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-green-50">Father Name</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-green-50">National ID</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-green-50">Ethnic</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-green-50">Religion</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-green-50">Address</th>
                
                {/* Mother Information Headers */}
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-yellow-50">Mother Name</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-yellow-50">National ID</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-yellow-50">Ethnic</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-yellow-50">Religion</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-yellow-50">Address</th>
                
                {/* Delivered By Headers */}
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-orange-50">Name</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-orange-50">Position</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-orange-50">Signature</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-orange-50">Address</th>
                
                {/* Birth Record Headers */}
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-purple-50">Delivery Type</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-purple-50">Weight</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-purple-50">Height</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-purple-50">Head Circumference</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-purple-50">Birth Defected</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-purple-50">Defect Description</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-purple-50">Mother Thumb</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-purple-50">Father Thumb</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-purple-50">Baby Foot</th>
                
                {/* Certified By Headers */}
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-indigo-50">Name</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-indigo-50">Position</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-indigo-50">Signature</th>
                <th className="border border-gray-300 px-1 py-1 text-xs text-gray-600 bg-indigo-50">Clinic Name</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {records.map((record, index) => {
                const birthDate = record.birth_date ? new Date(record.birth_date) : null;

                const birthTime = (() => {
                  if (record.birth_time_hour === null || record.birth_time_hour === undefined || record.birth_time_hour === '' || record.birth_time_minute === null || record.birth_time_minute === undefined || record.birth_time_minute === '') {
                    return '-';
                  }

                  return `${record.birth_time_hour}:${String(record.birth_time_minute).padStart(2, '0')} ${record.birth_time_ampm || 'AM'}`;
                })();
                
                return (
                  <tr key={record.id || index} className="hover:bg-gray-50 transition-colors duration-150">
                    {/* Basic Information */}
                    <td className="border border-gray-300 px-2 py-2 text-center font-medium text-sm">{((pagination.page - 1) * pagination.limit) + index + 1}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm truncate max-w-32" title={record.organization_name}>{record.organization_name || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm truncate max-w-24" title={record.district_name}>{record.district_name || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm truncate max-w-24" title={record.township_name}>{record.township_name || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm truncate max-w-28" title={record.clinic_name}>{record.clinic_name || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm whitespace-nowrap">{formatDate(record.registration_date)}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm font-medium text-blue-600">{record.registration_id || 'N/A'}</td>
                    
                    {/* Baby Information */}
                    <td className="border border-gray-300 px-2 py-2 text-sm font-semibold bg-blue-25 truncate max-w-36" title={record.baby_name}>{record.baby_name || 'N/A'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-blue-25 text-center">{record.gender || 'N/A'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-blue-25 whitespace-nowrap">{birthDate ? formatDate(record.birth_date) : '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-blue-25 text-center">{record.week_of_day || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm text-center bg-blue-25 whitespace-nowrap">{birthTime}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-blue-25 font-medium text-center">{getBirthPlaceName(record.birth_place_choose)}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-blue-25 truncate max-w-24" title={record.birth_place_other_specify}>{record.birth_place_other_specify || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-blue-25 truncate max-w-24" title={
                      record.birth_place_district_name?.toLowerCase() === 'other' 
                        ? record.birth_place_other_village 
                        : (record.birth_place_village_name || record.birth_place_village)
                    }>
                      {record.birth_place_district_name?.toLowerCase() === 'other' 
                        ? (record.birth_place_other_village || '-')
                        : (record.birth_place_village_name || record.birth_place_village || '-')
                      }
                    </td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-blue-25 truncate max-w-24" title={
                      record.birth_place_district_name?.toLowerCase() === 'other' 
                        ? record.birth_place_other_township 
                        : (record.birth_place_township_name || record.birth_place_township)
                    }>
                      {record.birth_place_district_name?.toLowerCase() === 'other' 
                        ? (record.birth_place_other_township || '-')
                        : (record.birth_place_township_name || record.birth_place_township || '-')
                      }
                    </td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-blue-25 truncate max-w-24" title={
                      record.birth_place_district_name?.toLowerCase() === 'other' 
                        ? record.birth_place_other_district 
                        : (record.birth_place_district_name || record.birth_place_district)
                    }>
                      {record.birth_place_district_name?.toLowerCase() === 'other' 
                        ? (record.birth_place_other_district || '-')
                        : (record.birth_place_district_name || record.birth_place_district || '-')
                      }
                    </td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-blue-25 text-center">{record.birth_type || 'Single'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm text-center bg-blue-25">{record.twin_order || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm text-center bg-blue-25">{record.birth_order || '-'}</td>
                    
                    {/* Father Information */}
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-green-25 truncate max-w-36" title={record.father_name}>{record.father_name || 'N/A'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-green-25 truncate max-w-28">{record.father_nrc_formatted || record.father_national_id || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-green-25 text-center">{record.father_ethnicity || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-green-25 text-center">{record.father_religion || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-green-25 truncate max-w-44" title={record.father_address}>{record.father_address || '-'}</td>
                    
                    {/* Mother Information */}
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-yellow-25 truncate max-w-36" title={record.mother_name}>{record.mother_name || 'N/A'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-yellow-25 truncate max-w-28">{record.mother_nrc_formatted || record.mother_nrc || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-yellow-25 text-center">{record.mother_ethnicity || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-yellow-25 text-center">{record.mother_religion || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-yellow-25 truncate max-w-44" title={record.mother_address}>{record.mother_address || '-'}</td>
                    
                    {/* Delivered By Information */}
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-orange-25 truncate max-w-36" title={record.delivered_by_name}>{record.delivered_by_name || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-orange-25 text-center truncate max-w-24">{record.delivered_by_position || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm text-center bg-orange-25">{record.delivered_by_signature ? 'Yes' : 'No'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-orange-25 truncate max-w-44" title={record.delivered_by_address}>{record.delivered_by_address || '-'}</td>
                    
                    {/* Birth Record Information */}
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-purple-25 text-center">{record.delivery_type || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm text-center bg-purple-25">{record.baby_weight || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm text-center bg-purple-25">{record.baby_height || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm text-center bg-purple-25">{record.baby_head_circumference || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm text-center bg-purple-25">{record.baby_birth_defected ? 'Yes' : 'No'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-purple-25" style={{maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={record.birth_defect_description || '-'}>{record.birth_defect_description || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm text-center bg-purple-25">{record.mother_right_thumb_print ? 'Yes' : 'No'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm text-center bg-purple-25">{record.father_right_thumb_print ? 'Yes' : 'No'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm text-center bg-purple-25">{record.baby_right_foot_print ? 'Yes' : 'No'}</td>
                    
                    {/* Certified By Information */}
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-indigo-25 truncate max-w-36" title={record.recorder_name}>{record.recorder_name || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-indigo-25 text-center truncate max-w-24">{record.recorder_position || '-'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm text-center bg-indigo-25">{record.recorder_signature ? 'Yes' : 'No'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-sm bg-indigo-25 truncate max-w-36" title={record.recorder_clinic_name}>{record.recorder_clinic_name || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* No Records Message */}
      {hasLoaded && !loading && records.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No records found</h3>
          <p className="mt-2 text-gray-500">
            {filters.district_id || filters.township_id || filters.clinic_id || filters.start_date || filters.end_date
              ? 'Try adjusting your search criteria.' 
              : 'No birth records have been added yet.'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {hasLoaded && !loading && pagination.pages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <span className="text-sm text-gray-700">
              Page {pagination.page} of {pagination.pages}
            </span>
            
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          
          <div className="text-sm text-gray-500">
            {pagination.total} total records
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchForm;
